import React, { useEffect } from 'react';
import { Drawer, Form, Input, Button, Row, Col, Space, notification, Tooltip, Select, Switch } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../redux/store';
import {
    fetchUserDetailRequest,
    updateUserRequest,
    clearUserDetail,
    fetchUserRolesRequest,
    fetchUsersRequest,
} from '../../../../redux/reducers/clients/clientsSlice';
import { UserUpdateData, UserRole } from '../../../../redux/reducers/clients/clients.types';
import { colors } from '../../../../utils/colors/colors';
import SearchableSelect from '../../../../components/SearchableSelect/SearchableSelect';

interface UpdateUserDrawerProps {
    open: boolean;
    onClose: () => void;
    userId?: string;
}

const UpdateUserDrawer: React.FC<UpdateUserDrawerProps> = ({ open, onClose, userId }) => {
    const { t } = useTranslation('clients');
    const [form] = Form.useForm<UserUpdateData>();
    const dispatch = useDispatch();

    const selectedUserRoleId = Form.useWatch('userRoleId', form);
    const anomalyMailEnabled = Form.useWatch('anomalyMailEnabled', form);
    const inactiveCounterMailEnabled = Form.useWatch('inactiveCounterMailEnabled', form);
    const batteryLevelMailEnabled = Form.useWatch('batteryLevelMailEnabled', form);

    const {
        userDetail: { data: userData, loading: userLoading, success: updateSuccess, error: updateError },
        userRoles: { data: userRolesData }
    } = useSelector((state: RootState) => state.clients);

    const adminRole = userRolesData?.find(role => role.name.toLowerCase() === 'admin');

    const isEditingAdmin = selectedUserRoleId === adminRole?.id;

    const handleSave = async () => {
        if (!userId) return;
        try {
            const values = await form.validateFields();
            const updateData: UserUpdateData = { ...values };

            if (!updateData.password) {
                delete updateData.password;
                delete updateData.confirmPassword;
            }
            dispatch(updateUserRequest({ userId, data: updateData }));
        } catch (error) {
            console.error('Form validation failed:', error);
        }
    };

    const handleClose = () => {
        form.resetFields();
        dispatch(clearUserDetail());
        onClose();
    };

    const genderOptions = [
        { value: 'male', label: t('male') },
        { value: 'female', label: t('female') },
        { value: 'other', label: t('other') }
    ];

    const userRoleOptions = userRolesData?.map((role: UserRole) => ({
        value: role.id,
        label: t(role.name)
    })) || [];

    const statusOptions = [
        { value: true, label: t('active') },
        { value: false, label: t('passive') }
    ];

    const statusTooltip = !userData?.companyIsActive ? t('company_is_passive_user_status_cannot_be_changed') : '';
    useEffect(() => {
        if (userId && open) {
            dispatch(fetchUserDetailRequest(userId));
            if (!userRolesData || userRolesData.length === 0) {
                dispatch(fetchUserRolesRequest());
            }
        }
    }, [userId, open, dispatch, userRolesData]);

    useEffect(() => {
        if (userData && open) {
            form.setFieldsValue({
                name: userData.name,
                surname: userData.surname,
                email: userData.email,
                gender: userData.gender,
                userRoleId: userData.userRoleId,
                isActive: userData.isActive,
                anomalyMailEnabled: userData.anomalyMailEnabled,
                inactiveCounterMailEnabled: userData.inactiveCounterMailEnabled,
                batteryLevelMailEnabled: userData.batteryLevelMailEnabled,

            });
        }
    }, [userData, open, form]);

    useEffect(() => {
        if (updateSuccess) {
            notification.success({ message: t('user_update_success') });
            if (userData?.companyId) {
                dispatch(fetchUsersRequest(userData.companyId));
            }
            handleClose();
        }
        if (updateError) {
            notification.error({ message: t(updateError) });
            dispatch(clearUserDetail());
        }
    }, [updateSuccess, updateError, t, dispatch, userData?.companyId]);
    return (
        <Drawer
            title={<span style={{ color: colors.blueitNavyBlue, fontWeight: 'bold' }}>{t("edit_user")}</span>}
            placement="right"
            size="large"
            onClose={handleClose}
            open={open}
            width={500}
            destroyOnClose
            footer={
                <Space style={{ float: 'right' }}>
                    <Button onClick={handleClose}>{t("cancel")}</Button>
                    <Button type="primary" onClick={handleSave} style={{ background: colors.blueitLightBlue }} loading={userLoading}>
                        {t("save")}
                    </Button>
                </Space>
            }
        >
            <Form form={form} layout="vertical" requiredMark={false}>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="name" label={t('name')} rules={[{ required: true, message: t('name_required') }]}>
                            <Input placeholder={t('enter_name')} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="surname" label={t('surname')} rules={[{ required: true, message: t('surname_required') }]}>
                            <Input placeholder={t('enter_surname')} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="email" label={t('email')} rules={[{ required: true, type: 'email', message: t('valid_email_required') }]}>
                            <Input placeholder={t('enter_mail_address')} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="gender" label={t('gender')} rules={[{ required: true, message: t('gender_required') }]}>
                            <SearchableSelect
                                placeholder={t('select_gender')}
                                options={genderOptions}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="userRoleId" label={t('user_role')} rules={[{ required: true, message: t('user_role_required') }]}>
                            <SearchableSelect
                                placeholder={t('select_user_type')}
                                options={userRoleOptions}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Tooltip title={statusTooltip} color={colors.blueitNavyBlue}>
                            <Form.Item name="isActive" label={t('status')} rules={[{ required: true, message: t('status_required') }]}>
                                <Select
                                    placeholder={t('select_status')}
                                    disabled={!userData?.companyIsActive}
                                    options={statusOptions}
                                />
                            </Form.Item>
                        </Tooltip>
                    </Col>
                </Row>

                {isEditingAdmin && (
                    <>
                        <h3 style={{ marginTop: 24, marginBottom: 16 }}>{t('notification_settings')}</h3>
                        <Row gutter={24} align="top">
                            <Col span={8} style={{ marginBottom: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Form.Item name="anomalyMailEnabled" valuePropName="checked" label={t('anomaly_mail_label')}>
                                        <Switch style={{ backgroundColor: anomalyMailEnabled ? colors.blueitLightBlue : '#00000040' }} />
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={8}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Form.Item name="inactiveCounterMailEnabled" valuePropName="checked" label={t('inactive_counter_mail_label')}>
                                        <Switch style={{ backgroundColor: inactiveCounterMailEnabled ? colors.blueitLightBlue : '#00000040' }} />
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={8}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Form.Item name="batteryLevelMailEnabled" valuePropName="checked" label={t('battery_level_mail_label')}>
                                        <Switch style={{ backgroundColor: batteryLevelMailEnabled ? colors.blueitLightBlue : '#00000040' }} />
                                    </Form.Item>
                                </div>
                            </Col>
                        </Row>
                    </>
                )}

                <h3 style={{ marginBottom: 16 }}>{t('change_password')}</h3>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="password"
                            label={t('new_password')}
                            hasFeedback
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (!value) return Promise.resolve();
                                        const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[./@$!%*?&=+\-_#^~|\\<>{}[\]:;"',()])/;
                                        if (regex.test(value)) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(t('passwordComplexity')));
                                    },
                                }
                            ]}
                        >
                            <Input.Password placeholder={t('enter_new_password')} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="confirmPassword"
                            label={t('confirm_new_password')}
                            dependencies={['password']}
                            hasFeedback
                            rules={[
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        const password = getFieldValue('password');
                                        if (!password) return Promise.resolve();
                                        if (!value) return Promise.reject(new Error(t('confirm_password_required')));
                                        if (password === value) return Promise.resolve();
                                        return Promise.reject(new Error(t("passwords_do_not_match")));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password placeholder={t('confirm_new_password')} />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Drawer>
    );
};

export default UpdateUserDrawer;